/**
 * @module HypothesisDeferredSection
 * @purpose Renders an explicit placeholder for deferred hypothesis card sections.
 * @depends CSS custom properties
 * @dependedOnBy HypothesisCard
 *
 * @rules
 * - Performance: pure display — props only, no state, no data fetching
 * - State: none
 * - Errors: n/a — purely presentational
 */

interface HypothesisDeferredSectionProps {
  title: string
  body: string
}

const CONTAINER_STYLE: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  padding: '10px 0',
}

const HEADING_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: '4px',
}

const BODY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-dim)',
  fontStyle: 'italic',
}

/**
 * Explicit placeholder for sections not yet analysed.
 * Always rendered — never silently absent.
 *
 * @behaviour Renders the section title and a deferred body message in a muted bordered container.
 * @errors n/a
 */
export function HypothesisDeferredSection({ title, body }: HypothesisDeferredSectionProps): React.ReactElement {
  return (
    <div style={CONTAINER_STYLE}>
      <div style={HEADING_STYLE}>{title}</div>
      <div style={BODY_STYLE}>{body}</div>
    </div>
  )
}
